import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, User, Bot, ChevronDown, Paperclip, FileText, Image, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SupportChatWidget = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list', 'new', 'chat'
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    category: 'course'
  });
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [activeTicket?.messages]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('elevate360_token');
      const response = await axios.get(`${API_URL}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // Validate minimum lengths
    if (newTicket.subject.trim().length < 3) {
      toast.error('Subject must be at least 3 characters');
      return;
    }
    
    if (newTicket.message.trim().length < 10) {
      toast.error('Message must be at least 10 characters');
      return;
    }

    try {
      setSending(true);
      const token = localStorage.getItem('elevate360_token');
      const response = await axios.post(
        `${API_URL}/api/support/tickets`,
        newTicket,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Safely access the ticket data
      const ticket = response.data?.ticket;
      if (ticket) {
        toast.success('Ticket created! Our team will respond soon.');
        setActiveTicket(ticket);
        setView('chat');
        setNewTicket({ subject: '', message: '', category: 'course' });
        fetchTickets();
      } else {
        toast.error('Unexpected response from server');
        console.error('Unexpected response:', response.data);
      }
    } catch (err) {
      console.error('Ticket creation error:', err);
      // Handle Pydantic validation errors (422) - detail might be an array or object
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Failed to create ticket';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation errors come as array
        errorMessage = errorDetail[0]?.msg || errorDetail[0]?.message || 'Validation error';
      } else if (typeof errorDetail === 'object' && errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !activeTicket) return;

    try {
      setSending(true);
      const token = localStorage.getItem('elevate360_token');
      
      // Use form data if there's a file
      if (selectedFile) {
        const formData = new FormData();
        formData.append('message', newMessage || 'Attached file');
        formData.append('file', selectedFile);
        
        const response = await axios.post(
          `${API_URL}/api/support/tickets/${activeTicket.ticket_id}/messages/with-attachment`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        
        setActiveTicket(response.data.ticket);
        setNewMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        const response = await axios.post(
          `${API_URL}/api/support/tickets/${activeTicket.ticket_id}/messages`,
          { message: newMessage },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setActiveTicket(response.data.ticket);
        setNewMessage('');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // 10MB limit
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const downloadAttachment = async (ticketId, attachment) => {
    try {
      const token = localStorage.getItem('elevate360_token');
      const response = await axios.get(
        `${API_URL}/api/support/attachments/${ticketId}/${attachment.attachment_id}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const isImageFile = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const openTicket = async (ticket) => {
    try {
      const token = localStorage.getItem('elevate360_token');
      const response = await axios.get(
        `${API_URL}/api/support/tickets/${ticket.ticket_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveTicket(response.data.ticket);
      setView('chat');
    } catch (err) {
      toast.error('Failed to load ticket');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  // Don't render if no user (not logged in)
  if (!user) return null;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#4c4c4c] hover:bg-[#5c5c5c] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        data-testid="support-chat-button"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <MessageCircle className="w-6 h-6" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-24 right-6 z-50 w-96 h-[500px] shadow-2xl flex flex-col" data-testid="support-chat-window">
          <CardHeader className="bg-[#4c4c4c] text-white rounded-t-lg py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Support
              </CardTitle>
              {view !== 'list' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setView('list');
                    setActiveTicket(null);
                  }}
                  className="text-white hover:bg-white/20"
                >
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
            {/* Ticket List View */}
            {view === 'list' && (
              <div className="flex flex-col h-full">
                <div className="p-3 border-b">
                  <Button
                    onClick={() => setView('new')}
                    className="w-full bg-[#D4AF37] hover:bg-[#c4a030] text-[#4c4c4c]"
                    data-testid="new-ticket-button"
                  >
                    + New Support Request
                  </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : tickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No support tickets yet</p>
                      <p className="text-sm">Create one if you need help!</p>
                    </div>
                  ) : (
                    tickets.map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        onClick={() => openTicket(ticket)}
                        className="p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        data-testid={`ticket-${ticket.ticket_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{ticket.subject}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {ticket.messages?.[ticket.messages.length - 1]?.content?.slice(0, 50)}...
                            </p>
                          </div>
                          <Badge className={`${getStatusColor(ticket.status)} text-xs`}>
                            {ticket.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(ticket.updated_at)}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* New Ticket View */}
            {view === 'new' && (
              <div className="p-4 space-y-4 overflow-y-auto">
                <div>
                  <label className="text-sm font-medium text-gray-700">Category</label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      variant={newTicket.category === 'course' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewTicket({ ...newTicket, category: 'course' })}
                      className={newTicket.category === 'course' ? 'bg-[#4c4c4c]' : ''}
                    >
                      Course Query
                    </Button>
                    <Button
                      type="button"
                      variant={newTicket.category === 'technical' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewTicket({ ...newTicket, category: 'technical' })}
                      className={newTicket.category === 'technical' ? 'bg-[#4c4c4c]' : ''}
                    >
                      Technical Issue
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Subject</label>
                  <Input
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                    className="mt-1"
                    data-testid="ticket-subject-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Message</label>
                  <Textarea
                    value={newTicket.message}
                    onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                    placeholder="Describe your issue or question in detail..."
                    className="mt-1 min-h-[120px]"
                    data-testid="ticket-message-input"
                  />
                </div>

                <Button
                  onClick={createTicket}
                  disabled={sending}
                  className="w-full bg-[#D4AF37] hover:bg-[#c4a030] text-[#4c4c4c]"
                  data-testid="submit-ticket-button"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </div>
            )}

            {/* Chat View */}
            {view === 'chat' && activeTicket && (
              <div className="flex flex-col h-full">
                {/* Ticket Header */}
                <div className="p-3 border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{activeTicket.subject}</p>
                      <p className="text-xs text-gray-500">
                        {activeTicket.category === 'technical' ? 'Technical Issue' : 'Course Query'}
                      </p>
                    </div>
                    <Badge className={getStatusColor(activeTicket.status)}>
                      {activeTicket.status}
                    </Badge>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {(activeTicket.messages || []).map((msg) => (
                    <div
                      key={msg?.message_id || Math.random()}
                      className={`flex gap-2 ${
                        msg?.sender_role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {msg?.sender_role !== 'user' && (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          msg?.sender_role === 'bot' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          {msg?.sender_role === 'bot' ? (
                            <Bot className="w-4 h-4 text-purple-600" />
                          ) : (
                            <User className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] p-3 rounded-lg ${
                          msg?.sender_role === 'user'
                            ? 'bg-[#4c4c4c] text-white'
                            : msg?.sender_role === 'bot'
                            ? 'bg-purple-50 text-gray-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="text-xs font-medium mb-1 opacity-70">
                          {msg?.sender_name || 'Unknown'}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg?.content || ''}</p>
                        
                        {/* Attachments */}
                        {msg?.attachments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachments.map((att) => (
                              <button
                                key={att?.attachment_id || Math.random()}
                                onClick={() => downloadAttachment(activeTicket.ticket_id, att)}
                                className={`flex items-center gap-2 p-2 rounded text-xs w-full ${
                                  msg?.sender_role === 'user' 
                                    ? 'bg-white/10 hover:bg-white/20' 
                                    : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                              >
                                {isImageFile(att?.filename) ? (
                                  <Image className="w-4 h-4" />
                                ) : (
                                  <FileText className="w-4 h-4" />
                                )}
                                <span className="truncate flex-1 text-left">{att?.filename || 'File'}</span>
                                <Download className="w-3 h-3" />
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-xs opacity-50 mt-1">{formatTime(msg?.created_at)}</p>
                      </div>
                      {msg?.sender_role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
                          <User className="w-4 h-4 text-[#4c4c4c]" />
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                {activeTicket.status !== 'closed' && (
                  <div className="p-3 border-t">
                    {/* Selected file preview */}
                    {selectedFile && (
                      <div className="flex items-center gap-2 mb-2 p-2 bg-gray-100 rounded text-sm">
                        <Paperclip className="w-4 h-4 text-gray-500" />
                        <span className="truncate flex-1">{selectedFile.name}</span>
                        <button 
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.zip"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        className="shrink-0"
                        title="Attach file"
                      >
                        <Paperclip className="w-4 h-4" />
                      </Button>
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        disabled={sending}
                        data-testid="chat-message-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sending || (!newMessage.trim() && !selectedFile)}
                        className="bg-[#4c4c4c] hover:bg-[#5c5c5c]"
                        data-testid="send-message-button"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default SupportChatWidget;
